import React, { useState, useRef } from 'react';
import { X, Save, Image, Upload, Loader2, Trash2, Globe, Languages, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (e) {
    return '';
  }
};

const InlineArticleEditor = ({ article, displaySection, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: article?.title || '',
    description: article?.description || '',
    content: article?.content || article?.description || '',
    // Multilingual fields
    title_en: article?.title_en || article?.title || '',
    title_rw: article?.title_rw || '',
    description_en: article?.description_en || article?.description || '',
    description_rw: article?.description_rw || '',
    content_en: article?.content_en || article?.content || article?.description || '',
    content_rw: article?.content_rw || '',
    category: article?.category || 'top_stories',
    source: article?.source || 'YEGO',
    published_at: formatDateForInput(article?.published_at || article?.created_at),
    image_url: article?.image_url || '',
    content_images: article?.content_images || []
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingInline, setUploadingInline] = useState(false);
  const [activeLanguageTab, setActiveLanguageTab] = useState('en');
  const contentEnRef = useRef(null);
  const contentRwRef = useRef(null);
  const fileInputRef = useRef(null);
  const inlineFileInputRef = useRef(null);

  const isExternalArticle = !article?.is_user_created && !article?.is_localized;

  // Handle cover image upload
  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${BACKEND_URL}/api/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.image_url.startsWith('http') 
        ? response.data.image_url 
        : `${BACKEND_URL}${response.data.image_url}`;
      
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Handle inline image upload
  const handleInlineImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingInline(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${BACKEND_URL}/api/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.image_url.startsWith('http') 
        ? response.data.image_url 
        : `${BACKEND_URL}${response.data.image_url}`;
      
      const contentField = activeLanguageTab === 'en' ? 'content_en' : 'content_rw';
      const contentRef = activeLanguageTab === 'en' ? contentEnRef : contentRwRef;
      const textarea = contentRef.current;
      const currentContent = formData[contentField];
      const cursorPos = textarea?.selectionStart || currentContent.length;
      const textBefore = currentContent.substring(0, cursorPos);
      const textAfter = currentContent.substring(cursorPos);
      
      const imagePlaceholder = `\n[IMAGE:${imageUrl}]\n`;
      const newContent = textBefore + imagePlaceholder + textAfter;
      
      setFormData(prev => ({ 
        ...prev, 
        [contentField]: newContent,
        content_images: [...prev.content_images, imageUrl]
      }));
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingInline(false);
    }
  };

  // Remove inline image
  const removeInlineImage = (imageUrl) => {
    const placeholder = `[IMAGE:${imageUrl}]`;
    setFormData(prev => ({
      ...prev,
      content_en: prev.content_en.replace(placeholder, '').replace(/\n\n\n+/g, '\n\n'),
      content_rw: prev.content_rw.replace(placeholder, '').replace(/\n\n\n+/g, '\n\n'),
      content_images: prev.content_images.filter(img => img !== imageUrl)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Build data - only include non-empty fields
      const dataToSend = {
        title: formData.title_en || formData.title,
        description: formData.description_en || formData.description,
        content: formData.content_en || formData.content,
        title_en: formData.title_en,
        description_en: formData.description_en,
        content_en: formData.content_en,
        category: formData.category,
        source: formData.source,
      };
      
      // Only include optional fields if they have values
      if (formData.title_rw?.trim()) dataToSend.title_rw = formData.title_rw;
      if (formData.description_rw?.trim()) dataToSend.description_rw = formData.description_rw;
      if (formData.content_rw?.trim()) dataToSend.content_rw = formData.content_rw;
      if (formData.image_url) dataToSend.image_url = formData.image_url;
      if (formData.content_images?.length > 0) dataToSend.content_images = formData.content_images;
      if (formData.published_at) {
        dataToSend.published_at = new Date(formData.published_at).toISOString();
      }

      if (isExternalArticle) {
        // Localizing an external article - create new article in DB
        const response = await axios.post(`${BACKEND_URL}/api/articles/localize`, {
          ...dataToSend,
          display_section: displaySection,
          original_link: article.link
        });
        onSave?.(response.data, displaySection);
      } else {
        // Updating existing user/localized article
        const token = localStorage.getItem('yegoNewsToken');
        const response = await axios.put(
          `${BACKEND_URL}/api/admin/articles/${article.id}`,
          dataToSend,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          }
        );
        onSave?.(response.data, displaySection);
      }
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save article: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900 z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isExternalArticle ? 'Claim & Edit Article' : 'Edit Article'}
            </h3>
            {isExternalArticle && (
              <p className="text-sm text-emerald-500">
                This article will be saved to &quot;{displaySection}&quot; section
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Cover Image Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cover Image
            </label>
            <div className="flex gap-4">
              {formData.image_url ? (
                <div className="relative w-48 h-32 rounded-lg overflow-hidden group">
                  <img 
                    src={formData.image_url} 
                    alt="Cover" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 bg-white rounded-full"
                    >
                      <Upload className="h-4 w-4 text-gray-700" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                      className="p-2 bg-red-500 text-white rounded-full"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-48 h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-emerald-500 transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                  ) : (
                    <>
                      <Image className="h-6 w-6 text-gray-400" />
                      <span className="text-sm text-gray-500">Upload Cover</span>
                    </>
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverImageUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Language Toggle Section */}
          <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center gap-2 mb-3">
              <Languages className="h-5 w-5 text-emerald-600" />
              <span className="font-medium text-gray-900 dark:text-white">Multilingual Content</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Add translations for English and Kinyarwanda. Leave Kinyarwanda empty to fallback to English.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveLanguageTab('en')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                  activeLanguageTab === 'en'
                    ? 'bg-white dark:bg-gray-800 text-emerald-600 shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50'
                }`}
              >
                <Globe className="h-4 w-4" />
                English
              </button>
              <button
                type="button"
                onClick={() => setActiveLanguageTab('rw')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                  activeLanguageTab === 'rw'
                    ? 'bg-white dark:bg-gray-800 text-blue-600 shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50'
                }`}
              >
                <Globe className="h-4 w-4" />
                Kinyarwanda
              </button>
            </div>
          </div>

          {/* Hidden file input - shared between language tabs */}
          <input
            ref={inlineFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInlineImageUpload}
            className="hidden"
          />

          {/* English Content Tab */}
          {activeLanguageTab === 'en' && (
            <div className="space-y-4 animate-in fade-in duration-200">
              {/* English Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Headline (English)
                </label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={e => setFormData({ ...formData, title_en: e.target.value })}
                  className="w-full px-4 py-3 text-xl font-bold bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                  placeholder="Article headline in English..."
                  required
                />
              </div>

              {/* English Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Summary (English)
                </label>
                <textarea
                  value={formData.description_en}
                  onChange={e => setFormData({ ...formData, description_en: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
                  placeholder="Brief summary in English..."
                  required
                />
              </div>

              {/* English Content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Article Content (English)
                  </label>
                  <button
                    type="button"
                    onClick={() => inlineFileInputRef.current?.click()}
                    disabled={uploadingInline}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                  >
                    {uploadingInline ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Image className="h-4 w-4" />
                    )}
                    <span>Insert Image</span>
                  </button>
                </div>
                
                <textarea
                  ref={contentEnRef}
                  value={formData.content_en}
                  onChange={e => setFormData({ ...formData, content_en: e.target.value })}
                  rows={12}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none resize-none font-mono text-sm"
                  placeholder="Write your article content in English here..."
                />
              </div>
            </div>
          )}

          {/* Kinyarwanda Content Tab */}
          {activeLanguageTab === 'rw' && (
            <div className="space-y-4 animate-in fade-in duration-200">
              {/* Kinyarwanda Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Headline (Kinyarwanda)
                </label>
                <input
                  type="text"
                  value={formData.title_rw}
                  onChange={e => setFormData({ ...formData, title_rw: e.target.value })}
                  className="w-full px-4 py-3 text-xl font-bold bg-gray-50 dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Umutwe w'inkuru mu Kinyarwanda..."
                />
                {!formData.title_rw && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    ℹ️ If left empty, English title will be shown as fallback
                  </p>
                )}
              </div>

              {/* Kinyarwanda Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Summary (Kinyarwanda)
                </label>
                <textarea
                  value={formData.description_rw}
                  onChange={e => setFormData({ ...formData, description_rw: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                  placeholder="Incamake mu Kinyarwanda..."
                />
                {!formData.description_rw && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    ℹ️ If left empty, English summary will be shown as fallback
                  </p>
                )}
              </div>

              {/* Kinyarwanda Content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Article Content (Kinyarwanda)
                  </label>
                  <button
                    type="button"
                    onClick={() => inlineFileInputRef.current?.click()}
                    disabled={uploadingInline}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                  >
                    {uploadingInline ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Image className="h-4 w-4" />
                    )}
                    <span>Insert Image</span>
                  </button>
                </div>
                
                <textarea
                  ref={contentRwRef}
                  value={formData.content_rw}
                  onChange={e => setFormData({ ...formData, content_rw: e.target.value })}
                  rows={12}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none font-mono text-sm"
                  placeholder="Andika ibikubiye mu nkuru mu Kinyarwanda hano..."
                />
                {!formData.content_rw && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    ℹ️ If left empty, English content will be shown as fallback
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Content Images Preview */}
          {formData.content_images.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Inline Images ({formData.content_images.length})
              </label>
              <div className="flex flex-wrap gap-2">
                {formData.content_images.map((img, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden group">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeInlineImage(img)}
                      className="absolute inset-0 bg-red-500/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <Trash2 className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meta Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Source/Author
              </label>
              <input
                type="text"
                value={formData.source}
                onChange={e => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
                placeholder="Your name or brand"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <select
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
              >
                <option value="latest_news">Latest News</option>
                <option value="trending">Trending</option>
                <option value="explore_more">Explore More</option>
                <option value="for_you">For You</option>
                <option value="videos">Videos</option>
                <option value="local">Local</option>
              </select>
            </div>
          </div>

          {/* Publishing Date (Backdate Article) */}
          <div className="p-4 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40 rounded-xl space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                Publishing Date
              </label>
              <span className="text-[10px] uppercase tracking-wider bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 font-bold px-2 py-0.5 rounded-full">
                Edit Mode
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Assign a past publishing date (e.g., from last year) so this article is backdated in archives and feeds.
            </p>
            <input
              type="datetime-local"
              value={formData.published_at || ''}
              onChange={e => setFormData({ ...formData, published_at: e.target.value })}
              className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none text-sm font-medium"
            />
          </div>

          {/* Original Link Info */}
          {isExternalArticle && article.link && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Original source:</strong>{' '}
                <a href={article.link} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                  {article.link}
                </a>
              </p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-800 sticky bottom-0 bg-white dark:bg-gray-900">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {isExternalArticle ? 'Save & Claim' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InlineArticleEditor;
